/**
 * Placeholder type system — validation, defaults, type coercion.
 *
 * Supports 8 types: text, multiline, secret, number, boolean, enum, oauth, instance-picker.
 *
 * Each type defines:
 *   - Validation rules
 *   - Default value handling
 *   - Coercion from string input (CLI prompting returns strings)
 *
 * The `instance-picker` type queries the LocalIndex for available instances
 * matching a filter (kind, defRef prefix).
 *
 * @docLink packages/library/concepts#placeholder-system
 */

import type { Placeholder } from "@skaile/workspaces/types/manifests";
import type { IAssetIndex, InstanceFilter } from "../library.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Result of validating a single placeholder value.
 *
 * @docLink packages/library/concepts#placeholder-system
 */
export interface PlaceholderValidationResult {
  /** Whether the value passed validation. */
  valid: boolean;
  /** Validation error messages (empty when valid). */
  errors: string[];
  /** Coerced value (type-appropriate). */
  value?: unknown;
}

/**
 * Filter options for the `instance-picker` placeholder type.
 *
 * @docLink packages/library/concepts#placeholder-system
 */
export interface InstancePickerOptions {
  /** Filter instances by asset kind. */
  kind?: string;
  /** Filter instances by defRef prefix. */
  defRefPrefix?: string;
}

/**
 * A selectable instance entry returned by {@link queryInstancePickerChoices}.
 *
 * @docLink packages/library/concepts#placeholder-system
 */
export interface InstancePickerChoice {
  /** UUID of the selectable instance. */
  instanceId: string;
  /** Asset definition ref for the instance. */
  defRef: string;
  /** Human-readable display label. */
  label: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate a single placeholder value against its schema.
 *
 * @param placeholder - The placeholder definition.
 * @param value - The user-provided value (may be undefined if optional).
 * @returns Validation result with coerced value.
 * @docLink packages/library/concepts#placeholder-system
 */
export function validatePlaceholder(
  placeholder: Placeholder,
  value: unknown,
): PlaceholderValidationResult {
  const { key, type = "text", required } = placeholder;

  // Handle missing values
  if (value === undefined || value === null || value === "") {
    if (placeholder.default !== undefined) {
      return { valid: true, errors: [], value: placeholder.default };
    }
    if (required === true || required === undefined) {
      // Default: required unless explicitly set to false
      return { valid: false, errors: [`Placeholder "${key}" is required`] };
    }
    return { valid: true, errors: [], value: undefined };
  }

  // Type-specific validation
  switch (type) {
    case "text":
      return validateText(key, value);
    case "multiline":
      return validateMultiline(key, value);
    case "secret":
      return validateSecret(key, value);
    case "number":
      return validateNumber(key, value);
    case "boolean":
      return validateBoolean(key, value);
    case "enum":
      return validateEnum(key, value, placeholder.values);
    case "oauth":
      return validateOauth(key, value);
    case "instance-picker":
      return validateInstancePicker(key, value);
  }
}

/**
 * Validate all placeholders for an item.
 *
 * @param placeholders - Array of placeholder definitions.
 * @param inputs - Map of key to user-provided value.
 * @returns Map of key to validated value plus aggregate errors.
 * @docLink packages/library/concepts#placeholder-system
 */
export function validateAllPlaceholders(
  placeholders: Placeholder[],
  inputs: Record<string, unknown>,
): { valid: boolean; values: Record<string, unknown>; errors: string[] } {
  const values: Record<string, unknown> = {};
  const errors: string[] = [];

  for (const ph of placeholders) {
    const result = validatePlaceholder(ph, inputs[ph.key]);
    if (!result.valid) {
      errors.push(...result.errors);
    } else if (result.value !== undefined) {
      values[ph.key] = result.value;
    }
  }

  return { valid: errors.length === 0, values, errors };
}

/**
 * Query available instances for an `instance-picker` placeholder.
 *
 * @param library - The library to query.
 * @param options - Optional filter by kind or defRef prefix.
 * @returns Available instance choices for display in a CLI prompt.
 * @docLink packages/library/concepts#placeholder-system
 */
export async function queryInstancePickerChoices(
  library: IAssetIndex,
  options?: InstancePickerOptions,
): Promise<InstancePickerChoice[]> {
  const filter: InstanceFilter = {};
  if (options?.kind) {
    filter.kind = options.kind;
  }
  if (options?.defRefPrefix) {
    filter.defRef = options.defRefPrefix;
  }

  const instances = await library.listInstances(filter);

  return instances.map((inst) => ({
    instanceId: inst.id,
    defRef: inst.defRef,
    label: `${inst.defRef} (${inst.id.slice(0, 8)})`,
  }));
}

/**
 * Get the effective default value for a placeholder.
 *
 * @param placeholder - The placeholder definition.
 * @returns The declared default, or undefined if none.
 * @docLink packages/library/concepts#placeholder-system
 */
export function getDefaultValue(placeholder: Placeholder): unknown {
  return placeholder.default;
}

/**
 * Check if a placeholder holds a secret value (should be routed through SecretsRouter).
 *
 * Returns true for `secret` and `oauth` types.
 *
 * @param placeholder - The placeholder definition.
 * @docLink packages/library/concepts#placeholder-system
 */
export function isSecretPlaceholder(placeholder: Placeholder): boolean {
  return placeholder.type === "secret" || placeholder.type === "oauth";
}

// ---------------------------------------------------------------------------
// Type validators
// ---------------------------------------------------------------------------

function validateText(key: string, value: unknown): PlaceholderValidationResult {
  if (typeof value !== "string") {
    return { valid: false, errors: [`Placeholder "${key}" must be a string`] };
  }
  return { valid: true, errors: [], value };
}

function validateMultiline(key: string, value: unknown): PlaceholderValidationResult {
  if (typeof value !== "string") {
    return { valid: false, errors: [`Placeholder "${key}" must be a string`] };
  }
  return { valid: true, errors: [], value };
}

function validateSecret(key: string, value: unknown): PlaceholderValidationResult {
  if (typeof value !== "string") {
    return { valid: false, errors: [`Placeholder "${key}" must be a string`] };
  }
  if (value.length === 0) {
    return { valid: false, errors: [`Placeholder "${key}" (secret) cannot be empty`] };
  }
  return { valid: true, errors: [], value };
}

function validateNumber(key: string, value: unknown): PlaceholderValidationResult {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return { valid: true, errors: [], value };
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return { valid: true, errors: [], value: parsed };
    }
  }
  return { valid: false, errors: [`Placeholder "${key}" must be a valid number`] };
}

function validateBoolean(key: string, value: unknown): PlaceholderValidationResult {
  if (typeof value === "boolean") {
    return { valid: true, errors: [], value };
  }
  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (lower === "true" || lower === "1" || lower === "yes") {
      return { valid: true, errors: [], value: true };
    }
    if (lower === "false" || lower === "0" || lower === "no") {
      return { valid: true, errors: [], value: false };
    }
  }
  return { valid: false, errors: [`Placeholder "${key}" must be a boolean`] };
}

function validateEnum(
  key: string,
  value: unknown,
  allowedValues?: Array<string | number>,
): PlaceholderValidationResult {
  if (!allowedValues || allowedValues.length === 0) {
    return { valid: false, errors: [`Placeholder "${key}" (enum) has no allowed values defined`] };
  }
  const stringValue = String(value);
  const matches = allowedValues.some((v) => String(v) === stringValue);
  if (!matches) {
    return {
      valid: false,
      errors: [
        `Placeholder "${key}" value "${stringValue}" is not in allowed values: [${allowedValues.join(", ")}]`,
      ],
    };
  }
  // Return the original type from the allowed values
  const matched = allowedValues.find((v) => String(v) === stringValue);
  return { valid: true, errors: [], value: matched };
}

function validateOauth(key: string, value: unknown): PlaceholderValidationResult {
  // OAuth placeholders hold an opaque token/ref string
  if (typeof value !== "string") {
    return { valid: false, errors: [`Placeholder "${key}" (oauth) must be a string`] };
  }
  if (value.length === 0) {
    return { valid: false, errors: [`Placeholder "${key}" (oauth) cannot be empty`] };
  }
  return { valid: true, errors: [], value };
}

function validateInstancePicker(key: string, value: unknown): PlaceholderValidationResult {
  // instance-picker value is a UUID (instance id)
  if (typeof value !== "string") {
    return {
      valid: false,
      errors: [`Placeholder "${key}" (instance-picker) must be a string UUID`],
    };
  }
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidPattern.test(value)) {
    return {
      valid: false,
      errors: [`Placeholder "${key}" (instance-picker) must be a valid UUID`],
    };
  }
  return { valid: true, errors: [], value };
}
